import {
    Injectable, inject
} from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import {
    BulkResponse,
    BulkUtil,
    CreateRequest,
    DeleteRequest,
    DeleteResponse,
    GetResponse,
    RequestUtil,
    SearchResponse,
    UpdateRequest
} from "@shopliftr/common-js/shared";
import {
    ISearchSort,
    ModelAction
} from "@shopliftr/common-js/data";
import {
    Keyword, KeywordSearchFilterOptions
} from "@shopliftr/common-js/admin";
import { IFullAccessInternalApiClientService } from "../../../../interfaces/internal-api-client";
import { InternalApiRequestService } from "../../request/internal-api/internal-api-request.service";
import { ListUtil } from "../../../../list/util/list-util";

/**
 * API Client Service for Keyword entities.
 *
 * Implements full CRUD operations for Keyword entities via the Internal API v2.
 * Delegates HTTP communication to InternalApiRequestService while encapsulating
 * Keyword-specific request/response handling.
 *
 * @example
 * ```typescript
 * private readonly _keywordApiClientService = inject(KeywordApiClientService);
 *
 * this._keywordApiClientService.get$('keyword-123').subscribe(keyword => console.log(keyword));
 * this._keywordApiClientService.getAll$(new KeywordSearchFilterOptions()).subscribe(keywords => console.log(keywords));
 * this._keywordApiClientService.create$(keyword).subscribe(created => console.log(created));
 * this._keywordApiClientService.delete$(keyword.id, keyword.esVersion).subscribe(() => console.log('deleted'));
 * ```
 *
 * @pattern IFullAccessInternalApiClientService
 * @providedIn root
 * @implements {IFullAccessInternalApiClientService<Keyword>}
 */
@Injectable({ providedIn: "root" })
export class KeywordApiClientService implements IFullAccessInternalApiClientService<Keyword> {

    readonly basePath = "keyword";

    private readonly _internalApiRequestService = inject(InternalApiRequestService);

    /**
     * Retrieve multiple Keyword entities by their IDs in a single bulk request.
     *
     * @param ids Array of unique identifiers to retrieve
     * @returns Observable emitting a BulkResponse containing the requested entities
     */
    bulkGet$(ids: Array<string>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(ids.map((id: string) => {
            const request = RequestUtil.createGetRequest<Keyword>(id, Keyword.modelName, ModelAction.ClientGet);
            return BulkUtil.createRequestItem(request);
        }));
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Retrieve a single Keyword by its unique identifier.
     *
     * @param id The unique identifier of the entity to retrieve
     * @returns Observable emitting the Keyword entity
     */
    get$(id: string): Observable<Keyword> {
        const request = RequestUtil.createGetRequest<void>(id, Keyword.modelName, ModelAction.ClientGet);
        return this._internalApiRequestService.get$<Keyword>(request, this.basePath).pipe(
            map((response: GetResponse<Keyword>) => response.payload)
        );
    }

    /**
     * Retrieve all Keyword entities matching the provided filter criteria.
     *
     * @param filterOptions The search criteria to apply
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable emitting an array of all matching entities
     */
    getAll$(filterOptions: KeywordSearchFilterOptions, sort?: Array<ISearchSort>): Observable<Array<Keyword>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Keyword.modelName,
            undefined,
            undefined,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.getAll$(request, this.basePath);
    }

    /**
     * Search for Keyword entities with pagination support.
     *
     * @param filterOptions The search criteria to apply
     * @param page Zero-based page index for pagination (optional)
     * @param pageSize Number of items per page (optional)
     * @param sort Array of sort criteria to apply (optional)
     * @returns Observable emitting search results with pagination metadata
     */
    search$(
        filterOptions: KeywordSearchFilterOptions,
        page?: number,
        pageSize?: number,
        sort?: Array<ISearchSort>
    ): Observable<SearchResponse<Keyword>> {
        const request = RequestUtil.createSearchRequest(
            ModelAction.ClientSearch,
            Keyword.modelName,
            ListUtil.getFromValue(page, pageSize),
            pageSize,
            filterOptions,
            sort
        );
        return this._internalApiRequestService.search$(request, this.basePath);
    }

    /**
     * Create a new Keyword entity.
     *
     * @param fields The Keyword model containing the field values for the new entity
     * @returns Observable emitting the created Keyword entity
     */
    create$(fields: Keyword): Observable<Keyword> {
        const createRequest = RequestUtil.createCreateRequest<Keyword, void>(
            ModelAction.ClientCreate,
            fields.cloneForAction(ModelAction.ClientCreate)
        );
        return this._internalApiRequestService.create$(createRequest, this.basePath)
            .pipe(map((response) => response.payload));
    }

    /**
     * Create multiple Keyword entities in a single bulk request.
     *
     * @param items Array of create requests for keywords to create
     * @returns Observable emitting a BulkResponse with results of each create operation
     */
    bulkCreate$(items: Array<CreateRequest<Keyword, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Update a Keyword entity by ID and version.
     *
     * @param id Unique identifier of the keyword to update
     * @param version Version for optimistic concurrency control
     * @param fields Keyword model containing the updated field values
     * @param removeFields Optional array of field names to remove
     * @returns Observable emitting the updated Keyword entity
     */
    update$(id: string, version: number, fields: Keyword, removeFields?: Array<string>): Observable<Keyword> {
        const updateFields = fields.cloneForAction(ModelAction.ClientUpdate);
        const updateRequest: UpdateRequest<Keyword> = RequestUtil.createUpdateRequest(
            id, ModelAction.ClientUpdate, updateFields, version, removeFields
        );
        return this._internalApiRequestService.update$(updateRequest, this.basePath)
            .pipe(map((response) => response.payload));
    }

    /**
     * Update a Keyword entity by diffing original and updated models.
     *
     * @param original The original Keyword model
     * @param updated The Keyword model with changes applied
     * @returns Observable emitting the updated Keyword entity
     */
    updateWithChanges$(original: Keyword, updated: Keyword): Observable<Keyword> {
        return this._internalApiRequestService.updateWithChanges$(original, updated, this.basePath);
    }

    /**
     * Update multiple Keyword entities in a single bulk request.
     *
     * @param requests Array of update requests
     * @returns Observable emitting a BulkResponse with results of each update operation
     */
    bulkUpdate$(requests: Array<UpdateRequest<unknown, unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            requests.map((request) => BulkUtil.createRequestItem(request))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }

    /**
     * Delete a Keyword entity by ID and version.
     *
     * @param id Unique identifier of the keyword to delete
     * @param version Version for optimistic concurrency control
     * @returns Observable emitting the DeleteResponse confirming deletion
     */
    delete$(id: string, version: number): Observable<DeleteResponse> {
        const deleteRequest = RequestUtil.createDeleteRequest<Keyword>(
            id, Keyword.modelName, ModelAction.ClientDelete, version
        );
        return this._internalApiRequestService.delete$(deleteRequest, this.basePath);
    }

    /**
     * Delete multiple Keyword entities in a single bulk request.
     *
     * @param items Array of delete requests
     * @returns Observable emitting a BulkResponse with results of each delete operation
     */
    bulkDelete$(items: Array<DeleteRequest<unknown>>): Observable<BulkResponse> {
        const bulkRequest = BulkUtil.createBulkRequest(
            items.map((item) => BulkUtil.createRequestItem(item))
        );
        return this._internalApiRequestService.bulk$(bulkRequest);
    }
}
